import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { SwitchField } from '@clay-run/glaze/switch-field';
import { Text } from '@clay-run/glaze/text';
import { type HierarchyLensKey, type LensFlags } from '@shared/flow/account-hierarchy-filters';
import { DealsFilters, type DealsFiltersProps } from './DealsFilters';
import { RenewalsFilters, type RenewalsFiltersProps } from './RenewalsFilters';
import { FILTER_BAR_COPY, LENS_LABEL, rowCountLabel } from './hierarchyPresentation';

export interface HierarchySidebarProps {
  lensOn: LensFlags;
  onLensToggle: (lens: HierarchyLensKey, on: boolean) => void;
  /** Filters of the ON sections that are set away from their defaults. */
  activeCount: number;
  onResetAll: () => void;
  deals: DealsFiltersProps;
  renewals: RenewalsFiltersProps;
  /** The always-open Customer status section: the clickable legend (with its account list). */
  customerStatus: ReactNode;
  /** Table view only: "Show only matching" + "N of M accounts" (any section on). */
  table: { showOnlyMatching: boolean; onShowOnlyMatchingChange: (next: boolean) => void; matching: number; total: number } | null;
  /** Full screen: the Selected account card sits at the bottom. */
  footer?: ReactNode;
  /** Side-sheet mode: closes the sheet. */
  onClose?: () => void;
  className?: string;
}

function Section({ title, on, onChange, children }: { title: string; on: boolean; onChange: (on: boolean) => void; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-b border-border-primary py-3" aria-label={title}>
      <SwitchField
        label={<span className={clsx('text-sm', on ? 'font-semibold text-content-primary' : 'font-normal text-content-secondary')}>{title}</span>}
        checked={on}
        onCheckedChange={checked => onChange(checked)}
      />
      {on && <div className="flex flex-col gap-3">{children}</div>}
    </section>
  );
}

/** A section that is always open (no switch): its title is a plain heading. */
function StaticSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 py-3" aria-label={title}>
      <Text size="sm" weight="semibold" variant="primary">{title}</Text>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

/**
 * The Hierarchy tab's Filters sidebar, one body for Table, Graph and full screen. Three independent
 * sections: Deals and Renewals, each switched on to reveal its filters, and the always-open Customer
 * status key (its legend picks accounts to highlight); the ON sections and the key's selection combine. Presentational: state lives in `account-hierarchy-view-store`.
 */
export function HierarchySidebar({ lensOn, onLensToggle, activeCount, onResetAll, deals, renewals, customerStatus, table, footer, onClose, className }: HierarchySidebarProps) {
  return (
    <aside role="region" aria-label="Hierarchy filters" data-testid="hierarchy-sidebar" className={clsx('flex min-h-0 w-[288px] shrink-0 flex-col rounded-lg border border-border-primary bg-bg-primary', className)}>
      <div className="flex items-center gap-2 border-b border-border-primary px-3 py-2">
        <Text size="sm" weight="semibold" variant="primary">{FILTER_BAR_COPY.sidebarTitle}</Text>
        {activeCount > 0 && <Badge size="xs" color="blue" data-testid="hierarchy-active-count">{activeCount}</Badge>}
        <span className="ml-auto flex items-center gap-1">
          <Button type="button" size="sm" variant="ghost" disabled={activeCount === 0} onClick={onResetAll}>{FILTER_BAR_COPY.resetAll}</Button>
          {onClose && <Button type="button" size="sm" variant="ghost" aria-label="Close filters" onClick={onClose}>✕</Button>}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3">
        <Section title={LENS_LABEL.deals} on={lensOn.deals} onChange={on => onLensToggle('deals', on)}>
          <DealsFilters {...deals} />
        </Section>
        <Section title={LENS_LABEL.renewals} on={lensOn.renewals} onChange={on => onLensToggle('renewals', on)}>
          <RenewalsFilters {...renewals} />
        </Section>
        <StaticSection title={FILTER_BAR_COPY.customerStatusTitle}>
          {customerStatus}
        </StaticSection>
      </div>
      {table || footer ? (
        <div className="flex flex-col gap-2 border-t border-border-primary p-3">
          {table && (
            <div className="flex items-center justify-between gap-2">
              <SwitchField label={FILTER_BAR_COPY.showOnlyMatching} checked={table.showOnlyMatching} onCheckedChange={checked => table.onShowOnlyMatchingChange(checked)} />
              <Text size="xs" variant="tertiary" data-testid="hierarchy-row-count">{rowCountLabel(table.matching, table.total)}</Text>
            </div>
          )}
          {footer}
        </div>
      ) : null}
    </aside>
  );
}
